/**
 * Builds the same ItineraryData shape as plan_trail/services/itinerary.ts's
 * buildItinerary(), but from a saved hike's persisted HikeDay records (via
 * the GetHikeItinerary GraphQL query) instead of live route/marker geometry.
 * Powers the read-only Itinerary modal on the hike overview page.
 *
 * Differences from the live-planner builder, all following directly from
 * what this data source can and can't provide:
 *  - Each day already carries its OWN start/end item fields directly (the
 *    persisted model, unlike the planner's single movable trailStartDestination
 *    slot) — no "which day owns the trip start" resolution needed.
 *  - Start/end coordinates come from the day's own main-line planned trails'
 *    marker geometry (position-ordered, route_style === 'route' only, mirroring
 *    the live planner's stageStartForDay/stageEndForDay) rather than live route
 *    state — this is a saved hike, so the geometry is exactly what got saved.
 *  - No pace/sun data: durationSeconds/sunrise/sunset are always 0/null —
 *    the modal hides that UI when zero/null.
 */

import type { ItineraryData, ItineraryDayEntry } from '../../plan_trail/services/itinerary';
import type { StageDestination, StageDestinationItemType } from '../../plan_trail/services/stage-destinations';
import { wktPointToLatLng } from '../../../utils/helpers/WKTPointToLatLng';

export interface HikePlannedTrailMarkerFragment {
  position?: number | null;
  lonlat?: string | null;
}

export interface HikesPlannedTrailFragment {
  position: number;
  routeStyle?: string | null;
  ptv2PlannedTrail?: {
    markers?: HikePlannedTrailMarkerFragment[] | null;
  } | null;
}

export interface HikeDayFragment {
  dayNumber: number;
  name?: string | null;
  description?: string | null;
  totalDistanceMeters?: number | null;
  totalElevationGainMeters?: number | null;
  startItemType?: string | null;
  startItemId?: string | null;
  startItemName?: string | null;
  startItemPoiType?: string | null;
  endItemType?: string | null;
  endItemId?: string | null;
  endItemName?: string | null;
  endItemPoiType?: string | null;
  hikesPlannedTrails?: HikesPlannedTrailFragment[] | null;
}

// coordinates is required on StageDestination (used for live map attachment
// matching elsewhere) but is never read for display here — pointLabel() only
// ever reads .name. A day's ATTACHED destination doesn't carry its own
// coordinate from this query, so this is a harmless placeholder.
const UNUSED_COORDINATES: [number, number] = [0, 0];

function itemDestination(
  itemType: string | null | undefined,
  itemId: string | null | undefined,
  name: string | null | undefined,
  poiType: string | null | undefined
): StageDestination | null {
  if (!itemType || !name) return null;
  return {
    itemType: itemType as StageDestinationItemType,
    itemId: itemId || '',
    name,
    poiType: poiType || '',
    coordinates: UNUSED_COORDINATES,
  };
}

function isMainTrail(trail: HikesPlannedTrailFragment): boolean {
  return (trail.routeStyle || 'route') === 'route';
}

function mainTrailsByPosition(hikesPlannedTrails: HikesPlannedTrailFragment[] | null | undefined): HikesPlannedTrailFragment[] {
  return [...(hikesPlannedTrails || [])]
    .filter(isMainTrail)
    .sort((a, b) => a.position - b.position);
}

function markersByPosition(trail: HikesPlannedTrailFragment): HikePlannedTrailMarkerFragment[] {
  return [...(trail.ptv2PlannedTrail?.markers || [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

function parseCoordinates(lonlat: string | null | undefined): [number, number] | null {
  if (!lonlat) return null;
  const point = wktPointToLatLng(lonlat);
  return point ? [point.lng, point.lat] : null;
}

// The day's start: the first marker of its first main-line planned trail
// (ordered by position) — mirrors stage-destinations.ts's stageStartForDay.
function dayStartCoordinates(hikesPlannedTrails: HikesPlannedTrailFragment[] | null | undefined): [number, number] | null {
  const mains = mainTrailsByPosition(hikesPlannedTrails);
  if (mains.length === 0) return null;
  const markers = markersByPosition(mains[0]);
  return markers.length > 0 ? parseCoordinates(markers[0].lonlat) : null;
}

// The day's end: the last marker of its last main-line planned trail
// (ordered by position) — mirrors stage-destinations.ts's stageEndForDay.
function dayEndCoordinates(hikesPlannedTrails: HikesPlannedTrailFragment[] | null | undefined): [number, number] | null {
  const mains = mainTrailsByPosition(hikesPlannedTrails);
  if (mains.length === 0) return null;
  const markers = markersByPosition(mains[mains.length - 1]);
  return markers.length > 0 ? parseCoordinates(markers[markers.length - 1].lonlat) : null;
}

export function buildItineraryFromHikeDays(hikeDays: HikeDayFragment[]): ItineraryData {
  const sortedDays = [...hikeDays].sort((a, b) => a.dayNumber - b.dayNumber);

  const days: ItineraryDayEntry[] = sortedDays.map(day => ({
    dayNumber: day.dayNumber,
    name: day.name || `Day ${day.dayNumber}`,
    description: day.description || '',
    distanceMeters: day.totalDistanceMeters || 0,
    ascentMeters: day.totalElevationGainMeters || 0,
    durationSeconds: 0,
    sunrise: null,
    sunset: null,
    timeZone: null,
    startLabel: itemDestination(day.startItemType, day.startItemId, day.startItemName, day.startItemPoiType),
    startCoordinates: dayStartCoordinates(day.hikesPlannedTrails),
    endLabel: itemDestination(day.endItemType, day.endItemId, day.endItemName, day.endItemPoiType),
    endCoordinates: dayEndCoordinates(day.hikesPlannedTrails),
  }));

  const totalDistanceMeters = days.reduce((sum, day) => sum + day.distanceMeters, 0);
  const totalAscentMeters = days.reduce((sum, day) => sum + day.ascentMeters, 0);

  return {
    days,
    totalDistanceMeters,
    totalAscentMeters,
    totalDurationSeconds: 0,
    tripStart: days.length > 0 ? days[0].startLabel : null,
    tripStartCoordinates: days.length > 0 ? days[0].startCoordinates : null,
    tripEnd: days.length > 0 ? days[days.length - 1].endLabel : null,
    tripEndCoordinates: days.length > 0 ? days[days.length - 1].endCoordinates : null,
  };
}
